<script setup lang="ts">
export interface MissingTrimColor {
  name: string;
  chromeOptionCode: string | null;
  imageUrl: string | null;
  primaryRGBHexCode: string | null;
  msrp: number | null;
}

defineProps<{
  color: MissingTrimColor;
  baseMsrp: number | null;
  canMoveUp: boolean;
  canMoveDown: boolean;
}>();

defineEmits<{
  'move-up': [];
  'move-down': [];
}>();

const formatPrice = (price: unknown) => {
  if (price == null) return '—';
  const n = Number(price);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString()}`;
};

const paintMsrpNumber = (msrp: number | null) =>
  msrp != null && Number.isFinite(Number(msrp)) ? Number(msrp) : 0;

const totalPriceForColor = (baseMsrp: number | null, paintMsrp: number | null): number | null => {
  if (baseMsrp == null || !Number.isFinite(Number(baseMsrp))) return null;
  return Number(baseMsrp) + paintMsrpNumber(paintMsrp);
};

const colorImageUrl = (c: MissingTrimColor) => c.imageUrl || '/images/empty.png';
</script>

<template>
  <div
    class="trim-color-card tw-relative tw-h-auto tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-bg-white tw-shadow-sm tw-transition hover:tw-shadow-md"
  >
    <!-- Up / down — top-right, zero padding/gap between controls -->
    <div
      class="arrow-stack tw-absolute tw-right-1 tw-top-1 tw-z-10 tw-flex tw-flex-col tw-items-stretch tw-rounded tw-bg-white/95 tw-p-0 tw-leading-none tw-shadow-sm"
    >
      <v-btn
        type="button"
        icon
        variant="text"
        size="x-small"
        density="compact"
        class="arrow-stack__btn"
        :disabled="!canMoveUp"
        aria-label="Move color up"
        @click="$emit('move-up')"
      >
        <v-icon class="tw-m-0 tw-p-0 tw-leading-none" icon="fa fa-arrow-up" size="x-small" />
      </v-btn>
      <v-btn
        type="button"
        icon
        variant="text"
        size="x-small"
        density="compact"
        class="arrow-stack__btn"
        :disabled="!canMoveDown"
        aria-label="Move color down"
        @click="$emit('move-down')"
      >
        <v-icon class="tw-m-0 tw-p-0 tw-leading-none" icon="fa fa-arrow-down" size="x-small" />
      </v-btn>
    </div>

    <div class="tw-flex tw-w-full tw-flex-row tw-items-center tw-gap-0 tw-py-0">
      <!-- Left: image — only as tall/wide as the bitmap + tiny padding (no full-height gray block) -->
      <div
        class="tw-flex tw-shrink-0 tw-basis-[36%] tw-items-center tw-justify-center tw-self-center tw-bg-gray-100 tw-px-1 tw-py-0.5 tw-min-w-[44px] tw-max-w-[108px]"
      >
        <img
          :src="colorImageUrl(color)"
          alt=""
          class="tw-block tw-h-auto tw-max-h-[48px] tw-w-auto tw-max-w-full tw-object-contain"
        />
      </div>

      <!-- Right: content height drives card; rows are content-sized (no flex-1 fill) -->
      <div
        class="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-center tw-gap-0 tw-bg-white tw-py-0 tw-leading-none"
      >
        <!-- Row 1: Title (single line + ellipsis; full text in tooltip) -->
        <div class="tw-flex tw-min-w-0 tw-items-center tw-px-1.5 tw-py-0">
          <v-tooltip location="top" content-class="trim-title-tooltip">
            <template #activator="{ props: tipProps }">
              <div
                v-bind="tipProps"
                class="tw-min-w-0 tw-w-full tw-cursor-default tw-truncate tw-text-[11px] tw-font-semibold tw-leading-tight tw-text-gray-900"
              >
                {{ color.name }}
              </div>
            </template>
            <div class="tw-max-w-xs tw-text-xs tw-break-words">
              {{ color.name }}
            </div>
          </v-tooltip>
        </div>

        <!-- Row 2: preview + hex + chrome short code -->
        <div class="tw-flex tw-items-center tw-px-1.5 tw-py-0">
          <div
            class="tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-0.5 tw-text-[10px] tw-leading-tight tw-text-gray-700"
          >
            <span
              class="tw-inline-block tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-rounded-sm tw-border tw-border-gray-300 tw-bg-gray-50"
              :style="
                color.primaryRGBHexCode
                  ? { backgroundColor: `#${color.primaryRGBHexCode}` }
                  : undefined
              "
            />
            <span
              v-if="color.primaryRGBHexCode"
              class="tw-shrink-0 tw-tabular-nums tw-text-gray-700"
              >#{{ color.primaryRGBHexCode }}</span
            >
            <span
              v-if="color.chromeOptionCode"
              class="tw-min-w-0 tw-max-w-full tw-truncate tw-font-medium tw-text-gray-800"
              >({{ color.chromeOptionCode }})</span
            >
            <span
              v-if="!color.primaryRGBHexCode && !color.chromeOptionCode"
              class="tw-text-gray-400"
              >—</span
            >
          </div>
        </div>

        <!-- Row 3: total + calculation on one line -->
        <div class="tw-flex tw-items-center tw-px-1.5 tw-py-0">
          <div
            class="tw-flex tw-min-w-0 tw-w-full tw-flex-wrap tw-items-baseline tw-gap-x-1 tw-gap-y-0 tw-leading-tight"
          >
            <span
              class="tw-shrink-0 tw-text-[11px] tw-font-bold tw-tabular-nums tw-text-[#2E66FF]"
              >{{ formatPrice(totalPriceForColor(baseMsrp, color.msrp)) }}</span
            >
            <span class="tw-min-w-0 tw-text-[9px] tw-tabular-nums tw-text-gray-600">
              ({{ formatPrice(baseMsrp) }} + {{ formatPrice(paintMsrpNumber(color.msrp)) }})
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.arrow-stack__btn {
  min-height: 0 !important;
  min-width: 0 !important;
  height: 14px !important;
  width: 14px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 2px;
}
.arrow-stack__btn + .arrow-stack__btn {
  margin-top: 0 !important;
}
.arrow-stack :deep(.v-btn__content) {
  padding: 0 !important;
  line-height: 1 !important;
}
.arrow-stack :deep(.v-btn__overlay) {
  border-radius: 2px;
}
</style>
